/**
 * QuoteRequestSuccess.jsx
 * =======================
 * Confirmation après soumission d'une demande de devis entreprise.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Clock, ArrowRight, Home } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

export default function QuoteRequestSuccess() {
  const [params] = useSearchParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const id = params.get('id');
  const [qreq, setQreq] = useState(null);

  useEffect(() => {
    if (!id || !token) return;
    fetch(`${API}/enterprise/quote-requests/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setQreq)
      .catch(() => {});
  }, [id, token]);

  return (
    <div className="theme-light min-h-screen flex items-center justify-center p-4 bg-[#FBF9F3]">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-[#E8E5DE] p-8 text-center"
        data-testid="quote-request-success"
      >
        <div className="w-16 h-16 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-[#059669]" />
        </div>
        <h1 className="text-2xl font-display font-medium text-[#111827] mb-2">
          Demande transmise avec succès
        </h1>
        <p className="text-sm text-[#4B5563] leading-relaxed">
          Votre demande de devis a bien été enregistrée. Un membre de notre équipe commerciale revient
          vers vous sous <strong>48&nbsp;h ouvrées</strong> avec une proposition adaptée.
        </p>

        {qreq && (
          <div className="mt-6 bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl p-4 text-left space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium">Référence</span>
              <span className="text-sm font-mono text-[#111827]">{qreq.id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium">Type</span>
              <span className="text-sm text-[#111827]">
                {qreq.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium">Statut</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#059669] font-medium">
                <Clock className="w-3.5 h-3.5" />
                En analyse
              </span>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-2 text-left bg-white border border-[#E8E5DE] rounded-xl p-4">
          <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2 font-medium">Prochaines étapes</p>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#F5F0E3] text-[#92710A] text-[11px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
            <p className="text-sm text-[#4B5563]">Analyse de votre demande par notre équipe commerciale</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#F5F0E3] text-[#92710A] text-[11px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
            <p className="text-sm text-[#4B5563]">Envoi d'un devis personnalisé par email</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#F5F0E3] text-[#92710A] text-[11px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
            <p className="text-sm text-[#4B5563]">Validation en ligne ou upload de bon de commande</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => navigate('/')} data-testid="quote-success-home"
            className="flex-1 px-5 py-2.5 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-sm font-medium hover:bg-[#F8F7F4] inline-flex items-center justify-center gap-2">
            <Home className="w-4 h-4" /> Accueil
          </button>
          <Link to="/subscription/enterprise" data-testid="quote-success-other-offer"
            className="flex-1 px-5 py-2.5 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] inline-flex items-center justify-center gap-2">
            Autre offre <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="mt-5 text-[11px] text-[#6B7280] flex items-center justify-center gap-1.5">
          <Mail className="w-3 h-3" /> Une confirmation va être envoyée à votre adresse e-mail.
        </p>
      </motion.div>
    </div>
  );
}
